import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaExclamationCircle, FaSpinner, FaHeadset } from 'react-icons/fa'; // 👈 added FaHeadset
import { useAuth } from '../authContext/AuthProvider';
import '../styles/Auth.css';

const LoginScreen = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await login(formData.email, formData.password);
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Login successful!');
      const from = location.state?.from;
      if (from) {
        // Preserve both pathname and search parameters (e.g., ?phone=...)
        navigate(`${from.pathname}${from.search}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      // Clear previous errors
      setErrors({});
      
      // Handle structured error response
      if (typeof result.error === 'object') {
        // Set backend validation errors
        const backendErrors = {};
        Object.entries(result.error).forEach(([field, messages]) => {
          backendErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        // Handle non-field errors (like invalid credentials)
        if (result.error.non_field_errors) {
          backendErrors.non_field_error = result.error.non_field_errors[0];
        } else if (result.error.detail) {
          backendErrors.non_field_error = result.error.detail;
        }
        setErrors(backendErrors);
        if (backendErrors.non_field_error) {
          toast.error(backendErrors.non_field_error);
        }
      } else {
        // Handle string error message
        toast.error(result.error);
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          {/* 🎧 Headset logo icon */}
          <div className="auth-logo-icon">
            <FaHeadset size={34} />
          </div>
          <h1>Welcome Back</h1>
          <p>Please sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {errors.non_field_error && (
            <div className="error-text error-box">
              <FaExclamationCircle />
              {errors.non_field_error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className={errors.email ? 'is-invalid' : ''}
              disabled={isSubmitting}
              autoComplete="email"
            />
            {errors.email && (
              <div className="error-text">
                <FaExclamationCircle />
                {errors.email}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className={errors.password ? 'is-invalid' : ''}
              disabled={isSubmitting}
              autoComplete="current-password"
            />
            {errors.password && (
              <div className="error-text">
                <FaExclamationCircle />
                {errors.password}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spin" />
                <span>Signing in...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="auth-links">
            <Link to="/forgot-password">Forgot Password?</Link>
            <p>
              Don't have an account? <Link to="/signup">Sign Up</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginScreen;
